'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import Toggle from '@/components/ui/Toggle';
import type { RequestFormData } from './ServiceRequestWizard';

interface StepThreeProps {
  formData: RequestFormData;
  updateFormData: (updates: Partial<RequestFormData>) => void;
  onBack: () => void;
  onSubmit: (finalData: RequestFormData) => Promise<void>;
}

interface ContactFields {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  howHeard: string;
}

export default function StepThree({ formData, updateFormData, onBack, onSubmit }: StepThreeProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifUpdates, setNotifUpdates] = useState(formData.notificationUpdates ?? true);
  const [specialOffers, setSpecialOffers] = useState(formData.specialOffers ?? false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactFields>({
    defaultValues: {
      firstName: formData.firstName ?? '',
      lastName: formData.lastName ?? '',
      email: formData.email ?? '',
      phone: formData.phone ?? '',
      howHeard: formData.howHeard ?? '',
    },
  });

  const onFormSubmit = async (data: ContactFields) => {
    setIsSubmitting(true);
    const finalData: RequestFormData = {
      ...formData,
      ...data,
      notificationUpdates: notifUpdates,
      specialOffers: specialOffers,
    };
    updateFormData({
      ...data,
      notificationUpdates: notifUpdates,
      specialOffers: specialOffers,
    });
    await onSubmit(finalData);
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Your contact details</h2>
        <p className="text-sm text-muted-foreground mt-1">We'll send your quote to the email address below.</p>
      </div>

      {/* Name fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            placeholder="Hayley"
            {...register('firstName', { required: 'First name is required' })}
            className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring ${errors.firstName ? 'border-red-400' : 'border-input'}`}
          />
          {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
        </div>
        <div>
          <label htmlFor="lastName" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            placeholder="Thornton"
            {...register('lastName', { required: 'Last name is required' })}
            className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring ${errors.lastName ? 'border-red-400' : 'border-input'}`}
          />
          {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          Email Address <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">Your quote will be emailed here. A client account will be created if you accept.</p>
        <input
          id="email"
          type="email"
          placeholder="hayley.thornton@gmail.com"
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
          className={`w-full sm:w-96 px-3 py-2.5 text-sm border rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring ${errors.email ? 'border-red-400' : 'border-input'}`}
        />
        {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">In case we need to clarify job details before quoting.</p>
        <input
          id="phone"
          type="tel"
          placeholder="0412 345 678"
          {...register('phone', {
            required: 'Phone number is required',
            pattern: { value: /^[0-9\s\+\-\(\)]{8,15}$/, message: 'Enter a valid Australian phone number' },
          })}
          className={`w-full sm:w-64 px-3 py-2.5 text-sm border rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring ${errors.phone ? 'border-red-400' : 'border-input'}`}
        />
        {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
      </div>

      {/* How did you hear */}
      <div>
        <label htmlFor="howHeard" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
          How did you hear about us?
        </label>
        <select
          id="howHeard"
          {...register('howHeard')}
          className="w-full sm:w-80 px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">Select (optional)</option>
          <option value="google">Google Search</option>
          <option value="facebook">Facebook / Instagram</option>
          <option value="neighbour">Neighbour or friend</option>
          <option value="letterbox">Letterbox drop</option>
          <option value="repeat">Previous customer</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Notification preferences */}
      <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-3">
        <p className="text-xs font-semibold text-foreground tracking-wide uppercase mb-1">Communication Preferences</p>
        <Toggle
          id="notif-updates"
          checked={notifUpdates}
          onChange={(v) => { setNotifUpdates(v); updateFormData({ notificationUpdates: v }); }}
          label="Job update notifications"
          description="Receive email notifications when your job status changes — scheduling confirmations, team on-the-way alerts, and completion notices."
        />
        <Toggle
          id="special-offers"
          checked={specialOffers}
          onChange={(v) => { setSpecialOffers(v); updateFormData({ specialOffers: v }); }}
          label="Special offers & seasonal promotions"
          description="Occasional emails about discounts and seasonal service packages. Unsubscribe any time."
        />
      </div>

      {/* Terms notice */}
      <p className="text-xs text-muted-foreground leading-relaxed">
        By submitting this request you agree to our{' '}
        <a href="#" className="text-primary hover:underline font-medium">Terms of Service</a> and{' '}
        <a href="#" className="text-primary hover:underline font-medium">Privacy Policy</a>.
        Your details will only be used to process your service request and communicate about your job.
      </p>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground transition-all duration-150"
        >
          <Icon name="ArrowLeftIcon" size={16} variant="outline" />
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center gap-2 min-w-[180px] px-6 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Submitting Request...
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={16} variant="outline" />
              Submit Service Request
            </>
          )}
        </button>
      </div>
    </form>
  );
}